"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { useRealtimeKitMeeting, useRealtimeKitSelector } from "@cloudflare/realtimekit-react";

import { useClassroomStore } from "../store/useClassroomStore";

import AudioVisualizer from "./AudioVisualizer";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import useUserStore from "@/features/auth/store/useUserStore";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { SidebarTrigger } from "@/components/ui/sidebar.tsx";

export default function PreJoinUI() {
  const { t } = useTranslation(["common", "classroom"]);
  const { user } = useUserStore();

  // Use Cloudflare hooks
  const { meeting } = useRealtimeKitMeeting();

  // Get native Cloudflare states
  const isAudioEnabled = useRealtimeKitSelector((m) => m.self?.audioEnabled ?? false);
  const isVideoEnabled = useRealtimeKitSelector((m) => m.self?.videoEnabled ?? false);
  const self = useRealtimeKitSelector((m) => m.self);

  const {
    selectedWebcam,
    setSelectedWebcam,
    selectedMic,
    setSelectedMic,
    selectedSpeaker,
    setSelectedSpeaker,
  } = useClassroomStore();

  const [isLoading, setIsLoading] = useState(true);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const isMobile = useIsMobile();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Device lists state
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDeviceInfo[]>([]);

  // Function to fetch devices using Cloudflare API
  const fetchDevices = async () => {
    if (!meeting) return;

    try {
      // Get all devices using Cloudflare API
      const audioDevices = await meeting.self.getAudioDevices();
      const videoDevices = await meeting.self.getVideoDevices();
      const speakerDevices = await meeting.self.getSpeakerDevices();

      setAudioInputs(audioDevices);
      setVideoInputs(videoDevices);
      setAudioOutputs(speakerDevices);

      // Set default devices if not already set
      if (videoDevices.length > 0 && !selectedWebcam) {
        setSelectedWebcam(videoDevices[0].deviceId);
      }

      if (audioDevices.length > 0 && !selectedMic) {
        setSelectedMic(audioDevices[0].deviceId);
      }

      if (speakerDevices.length > 0 && !selectedSpeaker) {
        setSelectedSpeaker(speakerDevices[0].deviceId);
      }

      setDevicesLoaded(true);
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  // Fetch available devices and set defaults
  useEffect(() => {
    const initializeDevices = async () => {
      try {
        setIsLoading(true);
        await fetchDevices();
        setIsLoading(false);
      } catch (error) {
        console.error("Error accessing media devices:", error);
        setIsLoading(false);
      }
    };

    initializeDevices();
  }, [meeting]);

  // Update video preview when video is enabled/disabled or device changes
  useEffect(() => {
    if (!meeting || !self) return;

    const updateVideoPreview = async () => {
      if (isVideoEnabled && selectedWebcam) {
        try {
          // Get the video track from Cloudflare's self object
          const videoTrack = self.videoTrack;

          if (videoTrack && videoRef.current) {
            // Create a new stream with the video track
            const stream = new MediaStream([videoTrack]);

            videoRef.current.srcObject = stream;
          }
        } catch (error) {
          console.error("Error updating video preview:", error);
        }
      } else {
        // Clear video preview
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      }
    };

    updateVideoPreview();
  }, [isVideoEnabled, selectedWebcam, meeting, self]);

  const toggleWebcam = async () => {
    if (meeting) {
      try {
        if (isVideoEnabled) {
          await meeting.self.disableVideo();
        } else {
          await meeting.self.enableVideo();
        }
      } catch (error) {
        console.error("Error toggling webcam:", error);
      }
    }
  };

  const toggleMic = async () => {
    if (meeting) {
      try {
        if (isAudioEnabled) {
          await meeting.self.disableAudio();
        } else {
          await meeting.self.enableAudio();
        }
      } catch (error) {
        console.error("Error toggling mic:", error);
      }
    }
  };

  const handleJoin = async () => {
    if (isJoining) return; // Prevent multiple clicks

    try {
      setIsJoining(true);
      // Use Cloudflare's meeting.join() method
      await meeting.join();
      console.log("PreJoinUI: Joining meeting with Cloudflare Realtime Kit");
      // Note: isJoining will stay true as the component will likely unmount after successful join
    } catch (error: any) {
      console.error("🚫 Failed to join meeting:", error);

      const store = useClassroomStore.getState();

      // Extract error code from the error object
      let errorCode = "0002"; // Default to join failed

      if (error?.code) {
        errorCode = error.code.toString().padStart(4, "0");
      } else if (error?.message) {
        // Try to extract error code from message if available
        const codeMatch = error.message.match(/error code[:\s]*(\d+)/i);

        if (codeMatch) {
          errorCode = codeMatch[1].padStart(4, "0");
        }
      }

      store.setMeetingError({
        code: errorCode,
        message: error?.message || "Failed to join meeting",
      });

      setIsJoining(false); // Reset on error so user can try again
    }
  };

  // When selecting a webcam or microphone
  const handleWebcamChange = async (deviceId: string) => {
    setSelectedWebcam(deviceId);

    if (meeting) {
      try {
        const device = videoInputs.find((d) => d.deviceId === deviceId);

        if (device) {
          await meeting.self.setDevice(device);
        }
      } catch (error) {
        console.error("Error changing webcam:", error);
      }
    }
  };

  const handleMicChange = async (deviceId: string) => {
    setSelectedMic(deviceId);

    if (meeting) {
      try {
        const device = audioInputs.find((d) => d.deviceId === deviceId);

        if (device) {
          await meeting.self.setDevice(device);
        }
      } catch (error) {
        console.error("Error changing microphone:", error);
      }
    }
  };

  const handleSpeakerChange = async (deviceId: string) => {
    setSelectedSpeaker(deviceId);

    if (meeting) {
      try {
        const device = audioOutputs.find((d) => d.deviceId === deviceId);

        if (device) {
          await meeting.self.setDevice(device);
        }
      } catch (error) {
        console.error("Error changing speaker:", error);
      }
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isMobile && (
        <div className="absolute top-2 left-2 z-50">
          <SidebarTrigger />
        </div>
      )}
      <motion.div
        key="prejoin"
        animate={{ opacity: 1 }}
        className="bg-background flex h-[100dvh] w-full flex-col items-center justify-center p-2 md:p-4"
        exit={{ opacity: 0, y: -20, transition: { duration: 0.3 } }}
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          animate={{ scale: 1 }}
          className="max-h-[calc(100dvh-1rem)] w-full max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl shadow-xl md:max-h-[calc(100dvh-2rem)] md:max-w-[calc(100vw-2rem)] xl:max-w-6xl"
          initial={{ scale: 0.95 }}
          style={{ minWidth: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {/* Main content container */}
          <div className="bg-background flex h-full w-full flex-col md:flex-row">
            {/* Left side - Video preview */}
            <motion.div
              animate={{ opacity: 1 }}
              className="bg-background relative flex min-w-0 flex-1 items-center justify-center overflow-hidden md:rounded-l-2xl"
              initial={{ opacity: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="relative h-[240px] w-full md:h-full md:min-h-[400px]">
                {isVideoEnabled && self?.videoTrack ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    className="h-full w-full scale-x-[-1] object-cover"
                  />
                ) : (
                  <div className="bg-side-accent flex h-full w-full items-center justify-center">
                    <div className="flex flex-col items-center justify-center">
                      {user?.avatarUrl ? (
                        <div className="border-background/20 h-20 w-20 overflow-hidden rounded-full border-4 shadow-lg md:h-24 md:w-24">
                          <img
                            alt={user?.firstName || t("common:guest")}
                            className="h-full w-full object-cover"
                            src={user.avatarUrl || "/placeholder.svg"}
                          />
                        </div>
                      ) : (
                        <div className="bg-background/90 text-accent-foreground border-background/20 flex h-20 w-20 items-center justify-center rounded-full border-4 text-3xl font-medium shadow-lg md:h-24 md:w-24 md:text-4xl">
                          {user?.firstName?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                      )}
                      <p className="text-accent-foreground mt-3 text-base font-medium md:text-lg">
                        {user?.firstName || t("common:guest")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Quick controls overlay */}
                <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 space-x-3 md:bottom-4 md:space-x-4">
                  <motion.button
                    className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors md:h-10 md:w-10 ${
                      isAudioEnabled
                        ? "bg-primary text-accent-foreground"
                        : "bg-destructive text-destructive-foreground"
                    }`}
                    title={isAudioEnabled ? t("classroom:muteMic") : t("classroom:unmuteMic")}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={toggleMic}
                  >
                    <Icon
                      height="16"
                      icon={isAudioEnabled ? "lucide:mic" : "lucide:mic-off"}
                      width="16"
                    />
                  </motion.button>

                  <motion.button
                    className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors md:h-10 md:w-10 ${
                      isVideoEnabled
                        ? "bg-primary text-accent-foreground"
                        : "bg-destructive text-destructive-foreground"
                    }`}
                    title={
                      isVideoEnabled ? t("classroom:disableCamera") : t("classroom:enableCamera")
                    }
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={toggleWebcam}
                  >
                    <Icon
                      height="16"
                      icon={isVideoEnabled ? "lucide:video" : "lucide:video-off"}
                      width="16"
                    />
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* Right side - Controls */}
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              className="bg-background border-border flex min-w-0 flex-col border-t md:max-w-[400px] md:border-t-0 md:border-l"
              initial={{ opacity: 0, x: 20 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex min-w-0 flex-1 flex-col p-4 md:p-6">
                <motion.h2
                  animate={{ opacity: 1, y: 0 }}
                  className="text-accent-foreground mb-4 text-lg font-bold md:mb-6 md:text-xl"
                  initial={{ opacity: 0, y: -10 }}
                  transition={{ delay: 0.1 }}
                >
                  {t("classroom:joinMeeting")}
                </motion.h2>

                <div className="flex flex-1 flex-col space-y-4">
                  {/* Device Selection */}
                  <div className="space-y-3">
                    {/* Camera Selection */}
                    <div className="min-w-0">
                      <Label
                        className="text-accent-foreground mb-2 block text-sm font-medium"
                        htmlFor="camera"
                      >
                        {t("common:camera")}
                      </Label>
                      {videoInputs && (
                        <Select
                          disabled={isLoading || videoInputs.length === 0}
                          value={selectedWebcam}
                          onValueChange={handleWebcamChange}
                        >
                          <SelectTrigger
                            className="border-border bg-background/80 text-accent-foreground h-10 w-full min-w-0 backdrop-blur-sm"
                            id="camera"
                          >
                            <SelectValue className="truncate overflow-hidden">
                              <span className="block max-w-full truncate">
                                {videoInputs.find((w) => w.deviceId === selectedWebcam)?.label ||
                                  t("classroom:selectCamera")}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-background/95 text-accent-foreground z-[9999] max-h-[200px] max-w-[90vw] min-w-[var(--radix-select-trigger-width)] backdrop-blur-sm">
                            {videoInputs.map((webcam) => (
                              <SelectItem
                                key={webcam.deviceId}
                                value={
                                  webcam.deviceId ||
                                  `camera-${webcam.label?.substring(0, 5) || Math.random().toString(36).substring(2, 7)}`
                                }
                              >
                                <div className="flex w-full min-w-0 items-center gap-2">
                                  <Icon
                                    className="flex-shrink-0"
                                    height="14"
                                    icon="lucide:video"
                                    width="14"
                                  />
                                  <span className="block min-w-0 flex-1 truncate">
                                    {webcam.label ||
                                      `${t("common:camera")} ${webcam.deviceId?.slice(0, 5) || "default"}`}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Microphone Selection */}
                    <div className="min-w-0">
                      <Label
                        className="text-accent-foreground mb-2 block text-sm font-medium"
                        htmlFor="microphone"
                      >
                        {t("common:microphone")}
                      </Label>
                      {audioInputs && (
                        <Select
                          disabled={isLoading || audioInputs.length === 0}
                          value={selectedMic}
                          onValueChange={handleMicChange}
                        >
                          <SelectTrigger
                            className="border-border bg-background/80 text-accent-foreground h-10 w-full min-w-0 backdrop-blur-sm"
                            id="microphone"
                          >
                            <SelectValue className="truncate overflow-hidden">
                              <span className="block max-w-full truncate">
                                {audioInputs.find((m) => m.deviceId === selectedMic)?.label ||
                                  t("classroom:selectMicrophone")}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-background/95 text-accent-foreground z-[9999] max-h-[200px] max-w-[90vw] min-w-[var(--radix-select-trigger-width)] backdrop-blur-sm">
                            {audioInputs &&
                              audioInputs.map((mic) => (
                                <SelectItem
                                  key={mic.deviceId}
                                  value={
                                    mic.deviceId ||
                                    `mic-${mic.label?.substring(0, 5) || Math.random().toString(36).substring(2, 7)}`
                                  }
                                >
                                  <div className="flex w-full min-w-0 items-center gap-2">
                                    <Icon
                                      className="flex-shrink-0"
                                      height="14"
                                      icon="lucide:mic"
                                      width="14"
                                    />
                                    <span className="block min-w-0 flex-1 truncate">
                                      {mic.label ||
                                        `${t("common:microphone")} ${mic.deviceId?.slice(0, 5) || "default"}`}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Speaker Selection */}
                    <div className="min-w-0">
                      <Label
                        className="text-accent-foreground mb-2 block text-sm font-medium"
                        htmlFor="speaker"
                      >
                        {t("common:speaker" as any)}
                      </Label>
                      {audioOutputs && (
                        <Select
                          disabled={isLoading || audioOutputs.length === 0}
                          value={selectedSpeaker}
                          onValueChange={handleSpeakerChange}
                        >
                          <SelectTrigger
                            className="border-border bg-background/80 text-accent-foreground h-10 w-full min-w-0 backdrop-blur-sm"
                            id="speaker"
                          >
                            <SelectValue className="truncate overflow-hidden">
                              <span className="block max-w-full truncate">
                                {audioOutputs.find((s) => s.deviceId === selectedSpeaker)?.label ||
                                  t("classroom:selectSpeaker" as any)}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-background/95 text-accent-foreground z-[9999] max-h-[200px] max-w-[90vw] min-w-[var(--radix-select-trigger-width)] backdrop-blur-sm">
                            {audioOutputs &&
                              audioOutputs.map((speaker) => (
                                <SelectItem
                                  key={speaker.deviceId}
                                  value={
                                    speaker.deviceId ||
                                    `speaker-${speaker.label?.substring(0, 5) || Math.random().toString(36).substring(2, 7)}`
                                  }
                                >
                                  <div className="flex w-full min-w-0 items-center gap-2">
                                    <Icon
                                      className="flex-shrink-0"
                                      height="14"
                                      icon="lucide:volume-2"
                                      width="14"
                                    />
                                    <span className="block min-w-0 flex-1 truncate">
                                      {speaker.label ||
                                        `${t("common:speaker" as any)} ${speaker.deviceId?.slice(0, 5) || "default"}`}
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>

                  {/* Compact Microphone Test */}
                  <div className="border-border rounded-lg border p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <Label className="text-accent-foreground text-xs font-medium">
                        {t("classroom:microphoneLevel")}
                      </Label>
                      <Icon
                        className={
                          isAudioEnabled ? "text-accent-foreground" : "text-muted-foreground"
                        }
                        height="12"
                        icon={isAudioEnabled ? "lucide:mic" : "lucide:mic-off"}
                        width="12"
                      />
                    </div>

                    <div className="relative h-6 w-full">
                      {isAudioEnabled ? (
                        <AudioVisualizer barCount={50} className="h-full w-full" />
                      ) : (
                        <div className="bg-background/50 absolute inset-0 flex items-center justify-center rounded-md backdrop-blur-sm">
                          <div className="text-muted-foreground flex items-center gap-1">
                            <Icon height="10" icon="lucide:mic-off" width="10" />
                            <span className="text-xs">{t("classroom:enableMicToTest")}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Spacer to push join button to bottom */}
                  <div className="flex-1" />

                  {/* Join Button */}
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className="flex-shrink-0"
                    initial={{ opacity: 0, y: 10 }}
                    transition={{ delay: 0.6 }}
                  >
                    <Button
                      className="bg-primary text-accent-foreground hover:bg-primary/90 w-full rounded-xl py-4 text-sm md:py-5 md:text-base"
                      disabled={isLoading || !devicesLoaded || isJoining}
                      onClick={handleJoin}
                    >
                      {isLoading
                        ? t("common:loading")
                        : isJoining
                          ? t("classroom:joining" as any)
                          : t("classroom:joinNow")}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
